import React, { useState, useEffect } from 'react';
import { useSite } from '../../context/SiteContext';
import { dbService } from '../../services/firebase';
import { YachtItem } from '../../types';
import { EmptySiteNotice } from '../common/EmptySiteNotice';
import {
  Anchor,
  Plus,
  Edit,
  Trash2,
  Users,
  CheckCircle2,
  AlertCircle,
  Wrench,
  Search,
  ExternalLink,
} from 'lucide-react';
import { YachtFormModal } from './YachtFormModal';

export const YachtList: React.FC<{ onNavigate?: (view: string) => void }> = ({ onNavigate }) => {
  const { activeSite } = useSite();
  const [yachts, setYachts] = useState<YachtItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('ALL');
  const [editingYacht, setEditingYacht] = useState<YachtItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const loadYachts = async () => {
    if (!activeSite) return;
    setLoading(true);
    const data = await dbService.getYachts(activeSite.id);
    setYachts(data);
    setLoading(false);
  };

  useEffect(() => {
    loadYachts();
  }, [activeSite?.id]);

  if (!activeSite) {
    return (
      <EmptySiteNotice
        title="Yat Filosu Yönetimi İçin Aktif Web Sitesi Gereklidir"
        description="Filo eklemek, motor yat, gulet ve katamaranları listelemek ve teknik detayları düzenlemek için lütfen bir web sitesi oluşturun veya mevcut sitelerinizden birini aktif yapın."
        onOpenCreateSite={() => onNavigate && onNavigate('sites')}
        onReturnToDashboard={() => onNavigate && onNavigate('dashboard')}
      />
    );
  }

  const handleDelete = async (yachtId: string) => {
    if (confirm('Bu yatı filonuzdan silmek istediğinizden emin misiniz?')) {
      await dbService.deleteYacht(activeSite.id, yachtId);
      loadYachts();
    }
  };

  const handleToggleStatus = async (yacht: YachtItem) => {
    const nextStatus =
      yacht.status === 'AVAILABLE'
        ? 'BOOKED'
        : yacht.status === 'BOOKED'
        ? 'MAINTENANCE'
        : 'AVAILABLE';
    await dbService.saveYacht(activeSite.id, { ...yacht, status: nextStatus as any });
    loadYachts();
  };

  const filteredYachts = yachts.filter((y) => {
    const q = (searchQuery || '').toLowerCase();
    const matchesSearch =
      (y.name || '').toLowerCase().includes(q) ||
      (y.portLocation ? y.portLocation.toLowerCase().includes(q) : false);
    const matchesType = selectedType === 'ALL' || y.type === selectedType;
    return matchesSearch && matchesType;
  });

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto space-y-6 animate-in fade-in select-none">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-cyan-600">
            <Anchor className="w-5 h-5" />
            <span className="text-xs font-bold uppercase tracking-wider">Yat Kiralama Portalı</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
            Lüks Yat Filosu Yönetimi
          </h1>
          <p className="text-xs sm:text-sm text-slate-500">
            Filonuzdaki tüm tekneleri ekleyin, günlük kiralama fiyatlarını ve kabin özelliklerini güncelleyin.
          </p>
        </div>

        <button
          onClick={() => {
            setEditingYacht(null);
            setIsModalOpen(true);
          }}
          className="px-4 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-bold text-xs shadow-lg shadow-cyan-500/20 transition flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Yeni Yat Ekle
        </button>
      </div>

      {/* Filter & Search Bar */}
      <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
          <input
            type="text"
            placeholder="Yat adı veya lokasyon ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-cyan-500"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          {['ALL', 'Superyacht', 'Motor Yacht', 'Gulet', 'Catamaran'].map((type) => (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                selectedType === type
                  ? 'bg-cyan-600 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {type === 'ALL' ? 'Tüm Filo' : type}
            </button>
          ))}
        </div>
      </div>

      {/* Yacht Cards Grid */}
      {loading ? (
        <div className="text-center py-12 text-xs text-slate-400">Yatlar yükleniyor...</div>
      ) : filteredYachts.length === 0 ? (
        <div className="p-12 text-center bg-white rounded-2xl border border-slate-200 space-y-3">
          <Anchor className="w-10 h-10 text-slate-300 mx-auto" />
          <h3 className="font-bold text-sm text-slate-700">Kayıtlı Yat Bulunamadı</h3>
          <p className="text-xs text-slate-400 max-w-sm mx-auto">
            Filonuza ilk yatınızı ekleyerek müşterilerinizin web sitenizden rezervasyon yapmasını sağlayın.
          </p>
          <button
            onClick={() => {
              setEditingYacht(null);
              setIsModalOpen(true);
            }}
            className="px-4 py-2 bg-cyan-600 text-white rounded-xl text-xs font-bold hover:bg-cyan-500 transition"
          >
            İlk Yatı Ekle
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredYachts.map((yacht) => (
            <div
              key={yacht.id}
              className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition flex flex-col justify-between"
            >
              <div className="relative h-52 overflow-hidden bg-slate-100">
                <img
                  src={yacht.coverImage}
                  alt={yacht.name}
                  className="w-full h-full object-cover hover:scale-105 transition duration-500"
                />
                <div className="absolute top-3 left-3">
                  <button
                    onClick={() => handleToggleStatus(yacht)}
                    className={`px-2.5 py-1 rounded-lg text-[10px] font-bold shadow-md uppercase tracking-wider ${
                      yacht.status === 'AVAILABLE'
                        ? 'bg-emerald-500 text-white'
                        : yacht.status === 'BOOKED'
                        ? 'bg-amber-500 text-white'
                        : 'bg-rose-500 text-white'
                    }`}
                  >
                    {yacht.status === 'AVAILABLE'
                      ? '✓ Müsait'
                      : yacht.status === 'BOOKED'
                      ? '🔒 Dolu / Rezerve'
                      : '⚙️ Bakımda'}
                  </button>
                </div>

                <div className="absolute top-3 right-3 px-2 py-1 rounded-lg bg-slate-900/80 backdrop-blur-md text-white text-[11px] font-bold">
                  {yacht.type}
                </div>

                <div className="absolute bottom-3 left-3 px-2.5 py-1 rounded-lg bg-cyan-600 text-white text-xs font-extrabold shadow">
                  {yacht.pricePerDay.toLocaleString('tr-TR')} {yacht.currency} / Gün
                </div>
              </div>

              <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                <div>
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-base text-slate-900">{yacht.name}</h3>
                    <span className="text-xs text-slate-500 font-medium">{yacht.portLocation}</span>
                  </div>
                  <p className="text-xs text-slate-500 line-clamp-2 mt-1">{yacht.description}</p>
                </div>

                <div className="grid grid-cols-4 gap-1 py-2 border-y border-slate-100 text-center text-xs">
                  <div>
                    <span className="text-slate-400 block text-[9px]">Kapasite</span>
                    <span className="font-bold text-slate-700">{yacht.capacity} Kişi</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[9px]">Kabin</span>
                    <span className="font-bold text-slate-700">{yacht.cabins}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[9px]">Uzunluk</span>
                    <span className="font-bold text-slate-700">{yacht.lengthMeters}m</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[9px]">Yıl</span>
                    <span className="font-bold text-slate-700">{yacht.yearBuilt}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <button
                    onClick={() => {
                      setEditingYacht(yacht);
                      setIsModalOpen(true);
                    }}
                    className="flex-1 py-2 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition flex items-center justify-center gap-1.5"
                  >
                    <Edit className="w-3.5 h-3.5" />
                    Düzenle
                  </button>
                  <button
                    onClick={() => handleDelete(yacht.id)}
                    className="p-2 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 transition"
                    title="Sil"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Modal */}
      {isModalOpen && (
        <YachtFormModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          yacht={editingYacht}
          onSave={() => {
            setIsModalOpen(false);
            loadYachts();
          }}
        />
      )}
    </div>
  );
};
